/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CircularLoader from 'components/loader/circular';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import {
  selectPageIsLoading,
  getResourceList,
  getResourceById,
} from 'store/page/pageSlice';
import { getUsers } from 'store/admin/adminSlice';
import { selectIsAuth } from 'store/auth/authSlice';
import { selectGlobalModal, setGlobalModal } from 'store/transitionSlice';
import style from './page.module.scss';
import { useModalResult } from 'hooks/useModalResult';
import { dropResult, selectResult } from 'store/page/pageSlice';
import {
  dropResult as dropResultAdmin,
  selectResult as selectResultAdmin,
} from 'store/admin/adminSlice';
import {
  dropResult as dropResultAuth,
  selectResult as selectResultAuth,
} from 'store/auth/authSlice';
import {
  dropResult as dropResultTags,
  selectResult as selectResultTags,
} from 'store/tagsSlice';
import { fetchAllTags, fetchTopTags } from 'store/tagsSlice';
import { useCurrentCategory } from 'hooks/useCategoryId';
import { useOffsetLimit } from 'hooks/useOffsetLimit';
import { ResourceSortType, Special, UsersSortType } from 'store/types';
import ScrollToTop from 'components/ScrollToTop';
import Header from 'components/header/header';
import Footer from 'components/footer/footer';
import { useStartTags } from 'hooks/useStartTags';
import {
  ADAPTIVE_SIMILAR_IMAGES_LIMIT,
  DESKTOP_SIMILAR_IMAGES_LIMIT,
  MIN_SCREEN_WIDTH,
  MIN_TABLET_WIDTH,
} from 'utils/utils';
import MobileTabBar from 'components/mobileTabBar/mobileTabBar';
import cx from 'classnames';
import { useViewport } from 'contexts/viewportContext';

const PAGES_WITH_MOBILE_TAB_BAR = ['photos', 'vectors', 'illustrations'];

export default function Page(props: {
  children: React.ReactNode;
  name: string;
  isAdminPage: boolean;
  isLoginPage: boolean;
}) {
  useStartTags();

  const [searchParams] = useSearchParams();
  const isAuth = useAppSelector(selectIsAuth);
  const isGlobalLoading =
    useAppSelector(selectPageIsLoading) || props.name === 'loading';
  const dispatch = useAppDispatch();
  const category = searchParams.get('category');
  const tags = searchParams.get('tags');
  const sort = searchParams.get('sort');
  const page = Number(searchParams.get('page') || 1);
  const globalModal = useAppSelector(selectGlobalModal);
  const currentCategory = useCurrentCategory();
  const offsetLimit = useOffsetLimit();
  const routeParams = useParams();
  const isLiked = useSearchParams()[0].get('only_liked');
  const isUploaded = useSearchParams()[0].get('only_uploaded');
  const { width } = useViewport();

  const isAdaptiveModeOn = width <= MIN_SCREEN_WIDTH;
  const isMobileAdaptiveModeOn = width <= MIN_TABLET_WIDTH;
  const shouldMobileTabBarRender =
    isMobileAdaptiveModeOn && PAGES_WITH_MOBILE_TAB_BAR.includes(props.name);
  const similarImagesLimit = isAdaptiveModeOn
    ? ADAPTIVE_SIMILAR_IMAGES_LIMIT
    : DESKTOP_SIMILAR_IMAGES_LIMIT;

  let special: Special = null;

  if (isLiked) {
    special = {
      name: 'only_liked',
      value: 'true',
    };
  }

  if (isUploaded) {
    special = {
      name: 'only_uploaded',
      value: 'true',
    };
  }

  useModalResult({
    setCurrentModal: (modal: null | React.ReactElement) => {
      dispatch(setGlobalModal(modal));
    },
    dropResult,
    selectResult,
  });
  useModalResult({
    setCurrentModal: (modal: null | React.ReactElement) => {
      dispatch(setGlobalModal(modal));
    },
    dropResult: dropResultAdmin,
    selectResult: selectResultAdmin,
  });
  useModalResult({
    setCurrentModal: (modal: null | React.ReactElement) => {
      dispatch(setGlobalModal(modal));
    },
    dropResult: dropResultAuth,
    selectResult: selectResultAuth,
  });
  useModalResult({
    setCurrentModal: (modal: null | React.ReactElement) => {
      dispatch(setGlobalModal(modal));
    },
    dropResult: dropResultTags,
    selectResult: selectResultTags,
  });

  useEffect(() => {
    switch (props.name) {
      case 'photos':
      case 'vectors':
      case 'illustrations':
        dispatch(
          getResourceList({
            _saveName: 'search',
            special: null,
            tags: null,
            category: currentCategory.id,
            ...offsetLimit,
            sort: (sort as ResourceSortType) || '-created_at',
          })
        );
        dispatch(fetchTopTags(null));
        break;
      case 'search':
        dispatch(
          getResourceList({
            _saveName: 'search',
            special: special,
            tags: searchParams.get('tags') || null,
            category: currentCategory.id,
            ...offsetLimit,
            sort: (sort as ResourceSortType) || '-created_at',
          })
        );
        dispatch(fetchTopTags(null));
        break;
      case 'mapping':
        dispatch(fetchTopTags(null));
        break;
      case 'cabinet':
        dispatch(
          getResourceList({
            _saveName: 'only_uploaded',
            special: {
              name: 'only_uploaded',
              value: 'true',
            },
            tags: null,
            category: currentCategory.id,
            offset: 0,
            limit: similarImagesLimit,
            sort: (sort as ResourceSortType) || 'default',
          })
        );
        dispatch(
          getResourceList({
            _saveName: 'only_liked',
            special: {
              name: 'only_liked',
              value: 'true',
            },
            tags: null,
            category: currentCategory.id,
            offset: 0,
            limit: similarImagesLimit,
            sort: '-liked_at',
          })
        );
        dispatch(fetchTopTags(null));
        break;
      case 'card':
        dispatch(
          getResourceById({
            _id: routeParams.cardId || '',
          })
        );
        dispatch(
          getResourceList({
            _saveName: 'similar_to',
            special: {
              name: 'similar_to',
              value: routeParams.cardId || '',
            },
            tags: null,
            category: null,
            offset: 0,
            limit: similarImagesLimit,
            sort: (sort as ResourceSortType) || 'default',
          })
        );
        dispatch(fetchTopTags(null));
        break;
      case 'upload':
      case 'tags':
        dispatch(fetchAllTags({}));
        dispatch(fetchTopTags(null));
        break;
      case 'requests':
      case 'admin':
        dispatch(
          getUsers({
            type: 'not_existing',
            ...offsetLimit,
            sort: (sort as UsersSortType) || 'default',
          })
        );
        break;
      case 'existing':
        dispatch(
          getUsers({
            type: 'existing',
            ...offsetLimit,
            sort: (sort as UsersSortType) || 'default',
          })
        );
        break;
    }
  }, [props.name, tags, sort, page, isAuth, routeParams.cardId, category]);

  return (
    <ScrollToTop>
      <div className={style.container}>
        {globalModal !== null && globalModal}
        {props.isAdminPage || props.isLoginPage ? (
          <>{props.children}</>
        ) : (
          <div className={cx(style.inner, 'adaptive-page__inner')}>
            <Header></Header>
            {props.children}
            <Footer></Footer>
            {shouldMobileTabBarRender && <MobileTabBar></MobileTabBar>}
          </div>
        )}

        {isGlobalLoading && (
          <>
            <CircularLoader className={style.loader}></CircularLoader>
            <div className={style.overlay}></div>
          </>
        )}
      </div>
    </ScrollToTop>
  );
}
